import { useState } from 'react';

import { Card, MonoLabel, Skeleton, StatusPill, cn, focusRing } from '@/design-system';
import {
    effectiveTextOnAccent,
    HEADING_BACKGROUND,
    ON_ACCENT_DARK,
    type Branding,
    type HouseStyle,
} from '@/entities/tenant';
import { darkenUntilReadable, formatDayLong } from '@/shared/lib';

import { coverCopy } from '../copy';

export interface CoverPreviewCardProps {
    branding: Branding;
    /** De kleuren zoals ze nu in de kiezer staan, ook als ze nog niet bewaard zijn. */
    houseStyle: HouseStyle;
    logoPreview: string | null;
    companyName: string;
}

type Side = 'report' | 'client';

/**
 * Voorbeeld van het rapport en de klantpagina (scherm 11, rechterkolom).
 *
 * Het beweegt mee met het logo en de kleuren links, zodat een keuze meteen
 * zichtbaar is in de twee dingen die de klant echt te zien krijgt: het voorblad
 * van zijn rapport en de pagina waarop hij het opent. De app zelf blijft
 * Smartkeur-groen — deze kleuren raken alleen wat naar buiten gaat.
 *
 * Het oordeel in het klantvoorbeeld staat er met opzet in: die kleur verandert
 * niet mee. "Voldoende" is bij elk keuringsbedrijf hetzelfde geel (de tokens
 * `--judgement-*`, ook in de PDF), want het zegt iets over het huis en niet over
 * het bedrijf.
 */
export function CoverPreviewCard({
    branding,
    houseStyle,
    logoPreview,
    companyName,
}: CoverPreviewCardProps) {
    const [side, setSide] = useState<Side>('report');
    const logoUrl = logoPreview ?? branding.logoUrl;
    const accent = houseStyle.accent;
    const accentText = effectiveTextOnAccent(houseStyle);
    const heading = houseStyle.heading ?? ON_ACCENT_DARK;
    /* Een link is tekst en moet op wit leesbaar blijven, net als in de API. */
    const link = darkenUntilReadable(accent, HEADING_BACKGROUND, ON_ACCENT_DARK);

    const logo = logoUrl ? (
        <img
            src={logoUrl}
            alt=""
            className="max-h-[46px] max-w-[92px] rounded-[4px] object-contain"
        />
    ) : (
        <Skeleton variant="hatch" width={92} height={46} radius={4} bordered />
    );

    return (
        <Card padding="lg" as="section" aria-labelledby="cover-preview-title">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2
                    id="cover-preview-title"
                    className="font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
                >
                    {coverCopy.title}
                </h2>

                <div className="flex gap-1" role="tablist" aria-label={coverCopy.title}>
                    <PreviewTab
                        label={coverCopy.reportTab}
                        selected={side === 'report'}
                        onSelect={() => setSide('report')}
                    />
                    <PreviewTab
                        label={coverCopy.clientTab}
                        selected={side === 'client'}
                        onSelect={() => setSide('client')}
                    />
                </div>
            </div>

            {side === 'report' ? (
                <div className="rounded-[8px] border border-[var(--border-default)] p-5">
                    {/* Zoals de kop van het voorblad: het logo links, de gegevens rechts. */}
                    <div
                        className="flex items-start justify-between gap-4 pb-3"
                        style={{ borderBottom: `2px solid ${accent}` }}
                    >
                        {logo}
                        <span className="text-right font-[family-name:var(--font-display)] text-[13px] leading-[1.3] font-semibold text-[var(--text-secondary)]">
                            {companyName}
                        </span>
                    </div>

                    <div className="mt-4">
                        <MonoLabel size={9} tone="muted">
                            {coverCopy.eyebrow}
                        </MonoLabel>
                    </div>

                    <p
                        style={{ color: heading }}
                        className="mt-2 font-[family-name:var(--font-display)] text-[19px] leading-[1.2] font-semibold tracking-[-0.02em]"
                    >
                        {coverCopy.sampleAddress}
                    </p>

                    <p
                        style={{ color: heading, borderBottom: `2px solid ${accent}` }}
                        className="mt-[14px] pb-1 font-[family-name:var(--font-display)] text-[13px] leading-[1.3] font-semibold"
                    >
                        {coverCopy.heading}
                    </p>

                    <Skeleton variant="hatch" height={64} radius={6} className="mt-[10px]" />

                    <div className="mt-[14px] flex items-center justify-between gap-3 text-[11px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                        <span className="truncate">{branding.reportFooterText ?? companyName}</span>
                        <span className="shrink-0">{formatDayLong(new Date())}</span>
                    </div>
                </div>
            ) : (
                <div className="overflow-hidden rounded-[8px] border border-[var(--border-default)]">
                    {/* Zoals de kop van de klantpagina: het logo links, de gegevens rechts. */}
                    <div className="flex items-center justify-between gap-4 bg-[var(--surface-card)] px-5 py-4">
                        {logo}
                        <span className="text-right font-[family-name:var(--font-display)] text-[13px] leading-[1.3] font-semibold text-[var(--text-secondary)]">
                            {companyName}
                        </span>
                    </div>
                    <div style={{ background: accent }} className="h-[3px]" />

                    <div className="bg-[var(--surface-page)] px-5 py-[18px]">
                        <p
                            style={{ color: heading }}
                            className="font-[family-name:var(--font-display)] text-[16px] leading-[1.25] font-semibold"
                        >
                            {coverCopy.sampleAddress}
                        </p>
                        <p className="mt-1 text-[12px] leading-[1.5] text-[var(--text-reading)]">
                            {coverCopy.clientIntro}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span
                                style={{ background: accent, color: accentText }}
                                className="rounded-[7px] px-3 py-[7px] text-[12px] font-semibold"
                            >
                                {coverCopy.clientButton}
                            </span>
                            {/* Het oordeel draagt de kleur van de keuring, niet die van het bedrijf. */}
                            <StatusPill judgement="yellow" size="xs" bordered weight="medium">
                                {coverCopy.sampleJudgement}
                            </StatusPill>
                        </div>

                        <p className="mt-3 text-[12px] leading-[1.5]">
                            <span style={{ color: link }} className="underline">
                                {coverCopy.clientLink}
                            </span>
                        </p>
                    </div>
                </div>
            )}

            <p className="mt-3 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {coverCopy.note}
            </p>
        </Card>
    );
}

function PreviewTab({
    label,
    selected,
    onSelect,
}: {
    label: string;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={onSelect}
            className={cn(
                'cursor-pointer rounded-[7px] border px-[10px] py-[5px] text-[12px] leading-[1.3]',
                selected
                    ? 'border-[var(--accent)] bg-[var(--accent-tint)] text-[var(--accent-dark)]'
                    : 'border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)]',
                focusRing,
            )}
        >
            {label}
        </button>
    );
}
